import React, { useEffect } from 'react';
import { 
    CheckCircle2, 
    ShieldCheck, 
    Rocket,
    Clock,
    Zap,
    Play
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border border-slate-700/70 bg-slate-800/60 p-6 shadow-xl ${className}`}>
        {children}
    </div>
);

const CheckRow = ({ children, highlight = false }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-indigo-400' : 'text-blue-300'}`} aria-hidden />
        <span className={`leading-relaxed ${highlight ? 'text-indigo-100 font-medium' : 'text-slate-200 text-sm'}`}>{children}</span>
    </li>
);

export default function VisuAIProposal() {
    useEffect(() => {
        document.title = 'Visu.ai | Founder-Led Content Engine';
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">
            
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                 <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                     <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span> Strategy Deck</span>
                 </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-24">
                
                {/* Hero */}
                <section className="text-center pt-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-sm font-semibold tracking-wide uppercase">
                        <Rocket className="w-4 h-4" /> Custom Proposal for Nishanth
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        The Founder-Led <br/> Content Engine
                    </h1>
                    <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                        Positioning you as the undisputed authority in retail tech and AI security across the UK and UAE—completely on autopilot.
                    </p>
                </section>

                {/* The "Zero-Friction" Commitment */}
                <section>
                    <div className="mb-8 p-8 rounded-3xl bg-gradient-to-br from-indigo-900/40 to-slate-900/80 border border-indigo-500/30 shadow-2xl relative overflow-hidden">
                        <div className="absolute -right-20 -top-20 opacity-5 pointer-events-none">
                            <Clock className="w-96 h-96" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 text-white">The "Zero-Friction" Operational Model</h2>
                        <p className="text-indigo-200 text-lg mb-8 max-w-2xl">
                            We know you are scaling a massive B2B enterprise. Your biggest bottleneck right now is finding the time and consistency to shoot videos. We solve this permanently.
                        </p>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                                <div className="flex items-center gap-3 mb-3">
                                    <Zap className="w-6 h-6 text-indigo-400" />
                                    <h3 className="text-xl font-bold">Clone Once, Scale Forever</h3>
                                </div>
                                <p className="text-slate-300 text-sm">
                                    You only need to provide a few quick recordings upfront. We train a high-fidelity AI avatar of your voice and likeness. From there, <strong>you never have to record a video again.</strong>
                                </p>
                            </div>

                            <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                                <div className="flex items-center gap-3 mb-3">
                                    <ShieldCheck className="w-6 h-6 text-indigo-400" />
                                    <h3 className="text-xl font-bold">15 Minutes Per Month</h3>
                                </div>
                                <p className="text-slate-300 text-sm">
                                    Send us your raw ideas (masked CCTV theft videos, UK statistics, SOPs). We script, produce, and edit 30 videos. Your only job is to spend 15 minutes reviewing the batch before we publish.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Strategy & Deliverables */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-black mb-4">The Omnipresence Strategy</h2>
                        <p className="text-slate-400">Total B2B market saturation in the UK and UAE.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        <Card className="flex flex-col relative h-full">
                            <div className="mb-6 border-b border-slate-700/70 pb-6">
                                <div className="text-xs tracking-widest font-bold text-slate-400 uppercase mb-2">The Deliverables</div>
                                <h3 className="text-2xl font-bold mb-2">Massive Volume. Zero Effort.</h3>
                            </div>
                            <ul className="space-y-4 mb-8 flex-grow">
                                <CheckRow><strong>30 AI-Generated Videos/Mo:</strong> High-end, educational hooks engineered to capture supermarket and retail owners.</CheckRow>
                                <CheckRow><strong>Multi-Platform Domination:</strong> Posting across LinkedIn, Instagram, YouTube Shorts, X, to reach decision-makers.</CheckRow>
                                <CheckRow><strong>TikTok Deployment:</strong> Critical for the UK market—we leverage the highest organic reach platform available.</CheckRow>
                                <CheckRow><strong>Content Repurposing:</strong> Carousel frameworks and stories to nurture warm traffic into sales conversions.</CheckRow>
                            </ul>
                        </Card>

                        <Card className="flex flex-col relative h-full border-indigo-500/50 bg-indigo-900/10 shadow-[0_0_40px_-15px_rgba(99,102,241,0.3)]">
                            <div className="mb-6 border-b border-slate-700/70 pb-6">
                                <div className="text-xs tracking-widest font-bold text-indigo-400 uppercase mb-2">The Guarantee</div>
                                <h3 className="text-2xl font-bold mb-2">The 10K Target</h3>
                            </div>
                            <ul className="space-y-4 mb-8 flex-grow">
                                <CheckRow highlight><strong>Minimum 10,000 Followers:</strong> Guaranteed within your 3-month program.</CheckRow>
                                <CheckRow highlight><strong>Data-backed Analytics:</strong> You get a hyper-targeted audience of retail business owners, not generic engagement.</CheckRow>
                                <CheckRow highlight><strong>The "One-Client" ROI Logic:</strong> If 1% of 10,000 followers converts, that's 100 deep leads. At a $10,000 to $50,000+ ACV, closing <strong>just 1 deal</strong> instantly pays for your entire marketing engine. Two deals makes it pure, high-leverage profit.</CheckRow>
                            </ul>
                        </Card>
                    </div>
                </section>

                {/* Evidence & Proof */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-black mb-4">Proof of Execution</h2>
                        <p className="text-slate-400">See the quality of our AI Avatars and what our clients have to say.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        <div className="space-y-6">
                            <h3 className="text-2xl font-bold">100% AI-Run Profiles</h3>
                            <a href="https://www.youtube.com/@scalingedtechs/shorts" target="_blank" rel="noreferrer" className="block p-5 rounded-2xl bg-slate-800/60 border border-slate-700/70 hover:border-indigo-400 transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="bg-indigo-500/20 p-3 rounded-xl text-indigo-400"><Play size={24} /></div>
                                    <div>
                                        <div className="font-bold text-lg text-white">Sachin Dahiya (ScaleEd)</div>
                                        <div className="text-sm text-slate-400">Onboarded last month. 1K+ views per video.</div>
                                    </div>
                                </div>
                            </a>
                            <a href="https://www.instagram.com/realkushdesai/" target="_blank" rel="noreferrer" className="block p-5 rounded-2xl bg-slate-800/60 border border-slate-700/70 hover:border-indigo-400 transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="bg-indigo-500/20 p-3 rounded-xl text-indigo-400"><Play size={24} /></div>
                                    <div>
                                        <div className="font-bold text-lg text-white">Kush Desai</div>
                                        <div className="text-sm text-slate-400">Check the hyper-realistic lip sync quality.</div>
                                    </div>
                                </div>
                            </a>
                        </div>

                        <div className="space-y-6">
                            <h3 className="text-2xl font-bold">Client Testimonials</h3>
                            <a href="https://www.instagram.com/reel/DWhBNWtP_27/" target="_blank" rel="noreferrer" className="block p-4 rounded-xl bg-slate-800/40 border border-slate-700 hover:bg-slate-800 transition-colors text-slate-200">
                                🔗 Growth & Lead Generation Results
                            </a>
                            <a href="https://www.instagram.com/reel/DWIhVlBv1Cu/" target="_blank" rel="noreferrer" className="block p-4 rounded-xl bg-slate-800/40 border border-slate-700 hover:bg-slate-800 transition-colors text-slate-200">
                                🔗 "They Run Everything Hands-Off"
                            </a>
                            <a href="https://www.instagram.com/reel/DUk86IJD8jy/" target="_blank" rel="noreferrer" className="block p-4 rounded-xl bg-slate-800/40 border border-slate-700 hover:bg-slate-800 transition-colors text-slate-200">
                                🔗 System & Consistency Reviews
                            </a>
                        </div>
                    </div>
                </section>

                {/* Investment */}
                <section className="pb-10">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-black mb-4">The Commercials</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                        <Card className="flex flex-col text-center justify-center py-10">
                            <div className="text-xs tracking-widest font-bold text-slate-400 uppercase mb-2">3-Month Program</div>
                            <div className="flex justify-center items-center gap-1 my-2">
                                <span className="text-4xl font-black text-white">₹6,00,000</span>
                            </div>
                            <p className="text-sm text-slate-400 mt-2">Core Content Engine</p>
                        </Card>

                        <Card className="flex flex-col text-center justify-center py-10 border-indigo-500/50 bg-indigo-900/10">
                            <div className="text-xs tracking-widest font-bold text-indigo-400 uppercase mb-2">One-Time Fee</div>
                            <div className="flex justify-center items-center gap-1 my-2">
                                <span className="text-4xl font-black text-white">₹50,000</span>
                            </div>
                            <p className="text-sm text-indigo-200 mt-2">AI Avatar Studio Setup & Training</p>
                        </Card>
                    </div>
                </section>

                <div className="h-20" />
            </main>
        </div>
    );
}
